import { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { api } from '../lib/api';
import type { CreSignal, CreScanRun, SignalStatus, SignalType } from '../types';
import SignalCard, { SIGNAL_TYPE_META } from '../components/signalRadar/SignalCard';

const SUBMARKETS = [
  'Ontario / Airport',
  'Fontana Corridor',
  'Rialto / Colton',
  'Moreno Valley',
  'San Bernardino Core',
  'Redlands / Yucaipa',
  'Coachella Valley',
  'Other IE',
];

const CATEGORY_ORDER: SignalType[] = ['expansion', 'distress', 'construction', 'debt', 'market'];

function timeAgo(date?: string | null): string {
  if (!date) return '—';
  const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

export default function SignalRadar() {
  const [signals, setSignals] = useState<CreSignal[]>([]);
  const [lastRun, setLastRun] = useState<CreScanRun | null>(null);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [actionMsg, setActionMsg] = useState('');
  const [filters, setFilters] = useState({
    signal_type: '', min_confidence: '', submarket: '', status: '', search: '',
  });

  const loadSignals = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = { limit: '200' };
      if (filters.signal_type) params.signal_type = filters.signal_type;
      if (filters.min_confidence) params.min_confidence = filters.min_confidence;
      if (filters.submarket) params.submarket = filters.submarket;
      if (filters.status) params.status = filters.status;
      if (filters.search) params.search = filters.search;
      const res = await api.listSignals(params);
      setSignals(res.signals || []);
    } catch (err) {
      console.error('Failed to load signals:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadRuns = useCallback(async () => {
    try {
      const res = await api.listScanRuns(5);
      setLastRun((res.runs && res.runs[0]) || null);
    } catch (err) {
      console.error('Failed to load runs:', err);
    }
  }, []);

  useEffect(() => { loadSignals(); }, [loadSignals]);
  useEffect(() => { loadRuns(); }, [loadRuns]);

  const handleScan = async () => {
    setScanning(true);
    setActionMsg('');
    try {
      const res = await api.runSignalScan();
      const r = res.result;
      if (r?.status === 'complete') {
        setActionMsg(`Scan complete — ${r.inserted} new signal${r.inserted === 1 ? '' : 's'} (${r.hotLeads} hot), ${r.searchCount} searches.`);
      } else {
        setActionMsg(`Scan failed: ${r?.error || 'unknown error'}`);
      }
      await Promise.all([loadSignals(), loadRuns()]);
    } catch (err: any) {
      setActionMsg(`Scan failed: ${err?.message || 'error'}`);
    } finally {
      setScanning(false);
    }
  };

  const handleStatus = async (id: string, status: SignalStatus) => {
    // Optimistic UI: dismissed → remove; reviewed → mark.
    const prev = signals;
    if (status === 'dismissed') {
      setSignals((s) => s.filter((x) => x.id !== id));
    } else {
      setSignals((s) => s.map((x) => (x.id === id ? { ...x, status } : x)));
    }
    try {
      await api.updateSignal(id, status);
    } catch (err) {
      console.error('Status update failed:', err);
      setSignals(prev); // rollback
      setActionMsg('Failed to update signal.');
    }
  };

  const setFilter = (key: keyof typeof filters, value: string) =>
    setFilters((f) => ({ ...f, [key]: value }));

  const hotLeads = signals.filter((s) => s.confidence >= 4);
  const others = signals.filter((s) => s.confidence < 4);
  const byCategory = (cat: SignalType) => others.filter((s) => s.signal_type === cat);

  const runStatusBadge = (() => {
    if (!lastRun) return null;
    const map: Record<string, string> = {
      complete: 'bg-success-wash text-success',
      running: 'bg-info-wash text-info',
      error: 'bg-red-100 text-red-700',
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[lastRun.status] || 'bg-gray-100 text-gray-600'}`}>
        {lastRun.status}
      </span>
    );
  })();

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-[28px] font-semibold uppercase leading-[1.06] tracking-[-.01em] text-gray-900 sm:text-[32px]">CRE Signal Radar</h1>
            <p className="text-sm text-gray-500 mt-1">
              Daily AI web-search scan for expansion, distress, construction, debt, and market signals across the Inland Empire and Coachella Valley.
            </p>
          </div>
        </div>

        {/* Control bar */}
        <div className="bg-white rounded-xl border p-4 flex flex-wrap items-center gap-4">
          <button
            onClick={handleScan}
            disabled={scanning}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 disabled:opacity-50 flex items-center gap-2"
          >
            {scanning ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Scanning… (~1-2 min)
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                Run Scan
              </>
            )}
          </button>

          {/* Last run status */}
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span className="font-medium text-gray-500">Last scan:</span>
            {lastRun ? (
              <>
                <span>{timeAgo(lastRun.created_at)}</span>
                {runStatusBadge}
                <span className="text-gray-400">·</span>
                <span>{lastRun.signals_found} new</span>
                <span className="text-gray-400">·</span>
                <span>{lastRun.hot_leads} hot</span>
              </>
            ) : (
              <span className="text-gray-400">never — run a scan to begin</span>
            )}
          </div>

          {actionMsg && (
            <span className={`text-sm ${actionMsg.startsWith('Scan failed') || actionMsg.startsWith('Failed') ? 'text-red-600' : 'text-success'}`}>
              {actionMsg}
            </span>
          )}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border p-4 flex flex-wrap items-center gap-3">
          <select
            value={filters.signal_type}
            onChange={(e) => setFilter('signal_type', e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="">All Categories</option>
            {CATEGORY_ORDER.map((c) => (
              <option key={c} value={c}>{SIGNAL_TYPE_META[c].label}</option>
            ))}
          </select>
          <select
            value={filters.min_confidence}
            onChange={(e) => setFilter('min_confidence', e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="">All Scores</option>
            <option value="5">5 only</option>
            <option value="4">4+</option>
            <option value="3">3+</option>
            <option value="2">2+</option>
          </select>
          <select
            value={filters.submarket}
            onChange={(e) => setFilter('submarket', e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="">All Submarkets</option>
            {SUBMARKETS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <select
            value={filters.status}
            onChange={(e) => setFilter('status', e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="">Active (not dismissed)</option>
            <option value="new">New</option>
            <option value="reviewed">Reviewed</option>
            <option value="dismissed">Dismissed</option>
          </select>
          <input
            type="text"
            placeholder="Search company / summary…"
            value={filters.search}
            onChange={(e) => setFilter('search', e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm flex-1 min-w-[180px]"
          />
        </div>

        {/* Body */}
        {loading ? (
          <div className="bg-white rounded-xl border p-12 text-center text-gray-400">Loading signals…</div>
        ) : signals.length === 0 ? (
          <div className="bg-white rounded-xl border p-12 text-center text-gray-400">
            No signals yet. Click "Run Scan" to pull today's intelligence.
          </div>
        ) : (
          <div className="space-y-8">
            {/* Hot leads */}
            {hotLeads.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <h2 className="text-lg font-semibold text-gray-900">🔥 Hot Leads</h2>
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-warning-wash text-warning">
                    {hotLeads.length} (score 4-5)
                  </span>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  {hotLeads.map((s) => (
                    <SignalCard key={s.id} signal={s} onStatus={handleStatus} highlight />
                  ))}
                </div>
              </div>
            )}

            {/* Grouped by category (scores 1-3) */}
            {CATEGORY_ORDER.map((cat) => {
              const items = byCategory(cat);
              if (items.length === 0) return null;
              const meta = SIGNAL_TYPE_META[cat];
              return (
                <div key={cat}>
                  <div className="flex items-center gap-2 mb-3">
                    <h2 className="text-base font-semibold text-gray-900">{meta.emoji} {meta.label}</h2>
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">{items.length}</span>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {items.map((s) => (
                      <SignalCard key={s.id} signal={s} onStatus={handleStatus} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
